import React, { PropsWithChildren } from 'react';

import { css, cx } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { useRouter } from '@tanstack/react-router';

import { FontSizes } from '@/assets/fonts';
import { Icons } from '@/assets/icons';
import { useStyles } from '@/hooks/useStyles';

export type PageContainersProps = PropsWithChildren<{
  /**
   * The id of the page container.
   * All the pages should have a unique id.
   */
  id: string;
  title: string;
  content?: React.ReactNode;
  back?: boolean;
  className?: string;
  titleClassName?: string;
}>;

export const PageContainer: React.FC<PageContainersProps> = ({
  id,
  title,
  back = false,
  content,
  className,
  titleClassName,
  children,
}) => {
  const styles = useStyles(makeStyles);
  const router = useRouter();

  return (
    <div id={id} className={cx(styles.container, className)}>
      <title>{`Steto - ${title}`}</title>
      <div id={`${id}-title`} className={cx(styles.titleRow, titleClassName)}>
        {back ? (
          <Icons.arrowLeft
            className={styles.goBack}
            onClick={() => router.history.back()}
          />
        ) : null}
        <Typography variant="h4">{title}</Typography>
        <div className={styles.content}>{content}</div>
      </div>
      {children}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
  `,
  titleRow: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  goBack: css`
    fill: ${theme.palette.grey[900]};
    width: calc(${theme.spacing(8)} * 2 + ${FontSizes.h4});
    height: calc(${theme.spacing(4)} * 2 + ${FontSizes.h4});
    padding: ${theme.spacing(2, 4)};
    margin-right: ${theme.spacing(8)};
    cursor: pointer;
    &:hover {
      background-color: ${theme.palette.background.paper};
      border-radius: ${theme.spacing(4)};
    }
  `,
  content: css`
    display: flex;
    flex-direction: row;
    flex: 1 1;
    justify-content: flex-end;
  `,
});
